/**
 * Prop dressing (pure, deterministic, seeded by room id): turns an ArchLayout + theme kits into
 * prop placements, wall decor, foliage, flames / light pools, floor decals and light shafts.
 *
 * Rules (art bible §4.3, §5):
 *  - every prop stands on a dressing ledge (or a corridor cheek), its whole footprint clear of
 *    the walkable floor, so nothing blocks paths, pack spawns or telegraphs
 *  - hero props first (authored hints, expanded by the theme), then clutter clusters along the
 *    ledges, then foliage; wall decor on far / side walls (near walls stay low and bare)
 *  - tall items never stand on near-side ledges (they'd hide the fight from the camera)
 *  - floor decals (moss, cracks, puddles) hug room edges, away from pack spawns and the entrance
 */
import type { DungeonDef, Vec2 } from '@keystone/sim';
import { hashString, seededRandom } from '../layout';
import type { DungeonTheme, KitItem, LightTemp, PropKit, RGB, WallItem } from '../themes/types';
import type { ArchLayout, PlacedHint, WallFacing } from './arch';
import { type Pt, distToSegment, pointInConvex } from './geom2d';

export type PropLayer = 'hero' | 'prop' | 'detail';

export interface Placement {
  /** Model id from the theme manifest, a legacy KayKit file, or 'proc:<name>'. */
  asset: string;
  pos: Pt;
  /** Base height (yards). */
  y: number;
  /** three.js rotation.y. */
  yaw: number;
  scale: number;
  /** Extra non-uniform scale (x, y, z) on top of `scale`. */
  stretch?: readonly [number, number, number];
  /** Tilt around local x (radians), e.g. a toppled urn. */
  tilt?: number;
  tint: RGB;
  layer: PropLayer;
  cluster: number;
  /** Footprint radius (yards). */
  r: number;
  /** Height (yards). */
  h: number;
  owner: string;
  castShadow: boolean;
}

export interface Flame {
  pos: Pt;
  y: number;
  w: number;
  h: number;
  cluster: number;
}

export interface Glow {
  pos: Pt;
  y: number;
  r: number;
  temp: LightTemp;
  strength: number;
  cluster: number;
}

export interface Shaft {
  pos: Pt;
  y: number;
  yaw: number;
  w: number;
  h: number;
  cluster: number;
}

export const DECAL_KINDS = ['moss', 'cracks', 'puddle', 'roots'] as const;
export type DecalKindName = (typeof DECAL_KINDS)[number];

export interface FloorDecal {
  pos: Pt;
  y: number;
  r: number;
  yaw: number;
  kind: DecalKindName;
  cluster: number;
  alpha: number;
}

/** A worn path between a room's doorways (flat floor strip, a different pattern). */
export interface FloorPath {
  pts: Pt[];
  width: number;
  floor: string;
  y: number;
  cluster: number;
  owner: string;
}

/** A band of another floor pattern along the inside of a room's edge. */
export interface FloorBorder {
  polys: Pt[][];
  width: number;
  floor: string;
  y: number;
  cluster: number;
  owner: string;
}

/** Debris islands keep this far from every pack spawn (yards). */
export const ISLAND_CLEAR = 8;

export interface Mosaic {
  pos: Pt;
  y: number;
  r: number;
  cluster: number;
}

export interface DressLayout {
  placements: Placement[];
  flames: Flame[];
  glows: Glow[];
  shafts: Shaft[];
  decals: FloorDecal[];
  mosaics: Mosaic[];
  paths: FloorPath[];
  borders: FloorBorder[];
  /** Spots kept free of clutter (doors, hero props, arches). */
  reserved: { pos: Pt; r: number }[];
}

/** Hero expansion hook: a theme turns one placed hint into placements, flames and glows. */
export interface HeroContext {
  arch: ArchLayout;
  rnd: () => number;
  out: DressLayout;
  /** Surface height at p (ledge top or floor). */
  surface(p: Pt): number;
  place(p: Omit<Placement, 'cluster' | 'owner'> & { cluster?: number; owner?: string }): void;
}
export type HeroExpander = (h: PlacedHint, ctx: HeroContext) => void;

export interface DressOptions {
  /** Theme hero expanders by hint kind. */
  heroes?: Readonly<Record<string, HeroExpander>>;
  /** Door footprints to keep clear (centre + radius). */
  reserved?: readonly { pos: Pt; r: number }[];
  /** Torch model + flame offset for wall torches. */
  torch?: { asset: string; y: number; off: number; scale: number; flame: readonly [number, number] };
}

/** three.js rotation.y that turns local +z toward the sim direction (dx, dy). */
export const yawToward = (dx: number, dy: number): number => Math.atan2(dx, -dy);

const lerpTint = (t: readonly [RGB, RGB] | undefined, k: number): RGB =>
  t ? [t[0][0] + (t[1][0] - t[0][0]) * k, t[0][1] + (t[1][1] - t[0][1]) * k, t[0][2] + (t[1][2] - t[0][2]) * k] : [1, 1, 1];

function pickWeighted<T extends { w: number }>(list: readonly T[], rnd: () => number): T | null {
  if (!list.length) return null;
  const total = list.reduce((s, x) => s + x.w, 0);
  let x = rnd() * total;
  for (const it of list) if ((x -= it.w) <= 0) return it;
  return list[list.length - 1]!;
}

export function dressDungeon(d: DungeonDef, theme: DungeonTheme, arch: ArchLayout, opts: DressOptions = {}): DressLayout {
  const out: DressLayout = { placements: [], flames: [], glows: [], shafts: [], decals: [], mosaics: [], paths: [], borders: [], reserved: [...(opts.reserved ?? [])] };
  const placed: { pos: Pt; r: number }[] = [];
  const free = (p: Pt, r: number): boolean => !placed.some((o) => Math.hypot(o.pos.x - p.x, o.pos.y - p.y) < o.r + r + 0.08) && !out.reserved.some((o) => Math.hypot(o.pos.x - p.x, o.pos.y - p.y) < o.r + r);
  const surface = (p: Pt): number => arch.ledgeTopAt(p) ?? arch.floorAt(p);
  /** The whole footprint is on a ledge (never on walkable floor or in a pool / wall). */
  const onLedge = (p: Pt, r: number): boolean => {
    if (arch.distToWalkable(p) < r + 0.25) return false;
    if (arch.ledgeTopAt(p) === null) return false;
    const k = r * 0.75;
    for (const [dx, dy] of [[k, 0], [-k, 0], [0, k], [0, -k]] as const) if (arch.ledgeTopAt({ x: p.x + dx, y: p.y + dy }) === null) return false;
    return !arch.pools.some((pl) => pointInConvex(p, pl.poly) || distToSegmentPoly(p, pl.poly) < r);
  };
  const push = (pl: Placement): void => {
    out.placements.push(pl);
    placed.push({ pos: pl.pos, r: pl.r });
  };

  // ---- hero props (authored hints) ---------------------------------------------------
  for (const h of arch.hints) {
    const rnd = seededRandom(hashString(`${h.kind}@${h.pos.x.toFixed(1)},${h.pos.y.toFixed(1)}`));
    if (h.spec.floorDecal) {
      out.mosaics.push({ pos: h.pos, y: h.y, r: h.scale, cluster: h.cluster });
      continue;
    }
    out.reserved.push({ pos: h.pos, r: Math.hypot(h.spec.half[0], h.spec.half[1]) * h.scale * 0.85 + 0.3 });
    const ctx: HeroContext = {
      arch,
      rnd,
      out,
      surface,
      // Sub-pieces whose footprint would touch the walkable floor are dropped.
      place: (p) => {
        if (arch.distToWalkable(p.pos) < p.r && !arch.inObstacle(p.pos)) return; // +dungeon2: obstacles (the fountain) are solid
        push({ cluster: h.cluster, owner: h.owner, ...p });
      },
    };
    const ex = opts.heroes?.[h.kind];
    if (ex) ex(h, ctx);
    if (h.spec.flame) out.flames.push({ pos: h.pos, y: h.y + h.spec.h * h.scale * 0.85, w: 1.1 * h.scale, h: 1.8 * h.scale, cluster: h.cluster });
    if (h.spec.light) out.glows.push({ pos: h.pos, y: h.y, r: 4.2 + 1.2 * h.scale, temp: h.spec.light, strength: h.spec.light === 'jade' ? 0.5 : 0.75, cluster: h.cluster });
  }

  // ---- per room: ledge clutter, foliage, wall decor, torches, decals, shafts -----------
  for (const room of arch.rooms) {
    const kit = theme.kits[room.style.archetype];
    if (!kit) continue;
    const rnd = seededRandom(hashString(room.id));
    const R = room.rect;
    if (R && arch.ledgeHeight > 0) {
      const O = { x0: R.x0 - room.margins.w, y0: R.y0 - room.margins.s, x1: R.x1 + room.margins.e, y1: R.y1 + room.margins.n };
      const sides: { a: Pt; b: Pt; out: Pt; depth: number }[] = [
        { a: { x: O.x0, y: R.y1 }, b: { x: O.x1, y: R.y1 }, out: { x: 0, y: 1 }, depth: room.margins.n },
        { a: { x: O.x1, y: R.y0 }, b: { x: O.x0, y: R.y0 }, out: { x: 0, y: -1 }, depth: room.margins.s },
        { a: { x: R.x0, y: R.y0 }, b: { x: R.x0, y: R.y1 }, out: { x: -1, y: 0 }, depth: room.margins.w },
        { a: { x: R.x1, y: R.y1 }, b: { x: R.x1, y: R.y0 }, out: { x: 1, y: 0 }, depth: room.margins.e },
      ];
      for (const s of sides) scatterBand(s, kit, room.id, room.cluster, rnd);
    }
    decorWalls(room.id, room.cluster, kit, rnd);
    floorDecals(room, kit, rnd);
    if (R && room.style.border) roomBorder(room, R, room.style.border);
    if (R && room.style.path) roomPaths(room, R, room.style.path);
    if (R) debrisIslands(room, R, kit, rnd);
    const nShaft = theme.shafts?.[room.id] ?? 0;
    if (R && nShaft > 0) {
      for (let i = 0; i < nShaft; i++) {
        const fx = 0.3 + rnd() * 0.4;
        const fy = 0.35 + rnd() * 0.4;
        const pos = { x: R.x0 + (R.x1 - R.x0) * fx, y: R.y0 + (R.y1 - R.y0) * fy };
        out.shafts.push({ pos, y: room.elevation, yaw: -0.5 + rnd() * 0.4, w: 5 + rnd() * 3, h: 18 + rnd() * 4, cluster: room.cluster });
      }
    }
  }

  // ---- +dungeon2: enclosed corridors get wall torches (alternating sides, ~9 yd apart) ----
  if (arch.enclosed && opts.torch) {
    const T = opts.torch;
    for (const c of arch.corridors) {
      const walls = arch.walls.filter((w) => w.owner === c.id && w.top - w.foot > 2.2);
      let k = 0;
      for (const w of walls) {
        const len = Math.hypot(w.b.x - w.a.x, w.b.y - w.a.y);
        const p = { x: (w.a.x + w.b.x) / 2, y: (w.a.y + w.b.y) / 2 };
        // Wall pieces are ~3 yd: light every third one, never at the mouths.
        if (len < 1.5 || k++ % 3 !== 1) continue;
        if (arch.rooms.some((r) => distToSegmentPoly(p, r.outer) < 2.5)) continue;
        if (out.reserved.some((o) => Math.hypot(o.pos.x - p.x, o.pos.y - p.y) < o.r + 1.2)) continue;
        const inward = { x: -w.n.x, y: -w.n.y };
        const y = w.foot + T.y;
        push({ asset: T.asset, pos: { x: p.x + w.n.x * T.off, y: p.y + w.n.y * T.off }, y, yaw: yawToward(inward.x, inward.y), scale: T.scale, tint: [1, 1, 1], layer: 'prop', cluster: c.cluster, r: 0.4, h: 1, owner: c.id, castShadow: false });
        out.flames.push({ pos: { x: p.x + inward.x * T.flame[0], y: p.y + inward.y * T.flame[0] }, y: y + T.flame[1], w: 0.55, h: 0.95, cluster: c.cluster });
        out.glows.push({ pos: { x: p.x + inward.x * 1.8, y: p.y + inward.y * 1.8 }, y: w.foot, r: 3.4, temp: 'warm', strength: 0.55, cluster: c.cluster });
      }
    }
  }

  // ---- corridor cheeks: sparse candles / offerings -----------------------------------
  const ckit = theme.kits['corridor'];
  if (ckit && arch.ledgeHeight > 0) {
    for (const c of arch.corridors) {
      const rnd = seededRandom(hashString(c.id));
      const { a, dir, len, width } = c.axis;
      const nrm = { x: -dir.y, y: dir.x };
      for (const sgn of [-1, 1]) {
        let t = 1 + rnd() * ckit.spacing;
        while (t < len - 1) {
          const off = width / 2 + 0.75;
          const p = { x: a.x + dir.x * t + nrm.x * off * sgn, y: a.y + dir.y * t + nrm.y * off * sgn };
          t += ckit.spacing * (0.7 + rnd() * 0.7);
          const it = pickWeighted(ckit.clutter, rnd);
          if (!it) continue;
          const sc = it.scale[0] + rnd() * (it.scale[1] - it.scale[0]);
          const r = it.r * sc;
          if (!onLedge(p, r) || !free(p, r)) continue;
          push({ asset: it.asset, pos: p, y: surface(p) + (it.lift ?? 0) * sc, yaw: rnd() * Math.PI * 2, scale: sc, tint: lerpTint(it.tint, rnd()), layer: it.detail ? 'detail' : 'prop', cluster: c.cluster, r, h: it.h * sc, owner: c.id, castShadow: false });
        }
      }
    }
  }
  return out;

  // -------------------------------------------------------------------------------------
  function scatterBand(s: { a: Pt; b: Pt; out: Pt; depth: number }, kit: PropKit, owner: string, cluster: number, rnd: () => number): void {
    if (s.depth < 1.3) return;
    const len = Math.hypot(s.b.x - s.a.x, s.b.y - s.a.y);
    const ux = (s.b.x - s.a.x) / len;
    const uy = (s.b.y - s.a.y) / len;
    const near = s.out.y < -0.6;
    let t = 0.8 + rnd() * kit.spacing * 0.5;
    while (t < len - 0.8) {
      const depth = s.depth * (0.3 + rnd() * 0.4);
      const c = { x: s.a.x + ux * t + s.out.x * depth, y: s.a.y + uy * t + s.out.y * depth };
      t += kit.spacing * (0.7 + rnd() * 0.6);
      if (rnd() < 0.12) continue;
      const n = kit.cluster[0] + Math.floor(rnd() * (kit.cluster[1] - kit.cluster[0] + 1));
      const spread = Math.min(1.8, s.depth * 0.45);
      for (let i = 0; i < n; i++) {
        let it = pickWeighted(kit.clutter, rnd);
        if (!it) break;
        if (near && it.h * it.scale[1] > 1.5) it = kit.clutter.find((x) => x.h * x.scale[1] <= 1.5) ?? null;
        if (!it) continue;
        tryPlace(it, { x: c.x + (rnd() - 0.5) * 2 * spread, y: c.y + (rnd() - 0.5) * 2 * spread }, owner, cluster, rnd, near);
      }
      if (rnd() < kit.foliageChance) {
        // Foliage hugs the wall base (outer half of the ledge).
        const f = pickWeighted(kit.foliage, rnd);
        const fd = s.depth * (0.65 + rnd() * 0.25);
        if (f) tryPlace(f, { x: s.a.x + ux * (t - kit.spacing * 0.4) + s.out.x * fd, y: s.a.y + uy * (t - kit.spacing * 0.4) + s.out.y * fd }, owner, cluster, rnd, near);
      }
    }
  }

  function tryPlace(it: KitItem, p: Pt, owner: string, cluster: number, rnd: () => number, near: boolean): boolean {
    const sc = it.scale[0] + rnd() * (it.scale[1] - it.scale[0]);
    const r = it.r * sc;
    if (near && it.h * sc > 1.5) return false;
    if (!onLedge(p, r) || !free(p, r)) return false;
    const yaw = it.yaw !== undefined ? (rnd() - 0.5) * 2 * it.yaw : rnd() * Math.PI * 2;
    push({ asset: it.asset, pos: p, y: surface(p) + (it.lift ?? 0) * sc, yaw, scale: sc, tint: lerpTint(it.tint, rnd()), layer: it.detail ? 'detail' : 'prop', cluster, r, h: it.h * sc, owner, castShadow: !it.detail && it.h * sc > 1.6 });
    return true;
  }

  function decorWalls(owner: string, cluster: number, kit: PropKit, rnd: () => number): void {
    const walls = arch.walls.filter((w) => w.owner === owner && !w.corridor);
    let acc = rnd() * kit.wallSpacing;
    let torchAcc = kit.torchSpacing > 0 ? rnd() * kit.torchSpacing : Infinity;
    for (const w of walls) {
      const len = Math.hypot(w.b.x - w.a.x, w.b.y - w.a.y);
      const ux = (w.b.x - w.a.x) / (len || 1);
      const uy = (w.b.y - w.a.y) / (len || 1);
      const inward = { x: -w.n.x, y: -w.n.y };
      const wallH = w.top - w.foot;
      for (let s = 0.4; s < len; s += 0.8) {
        acc -= 0.8;
        torchAcc -= 0.8;
        const p = { x: w.a.x + ux * s, y: w.a.y + uy * s };
        if (nearOpening(p, 2.6)) continue;
        if (out.reserved.some((o) => Math.hypot(o.pos.x - p.x, o.pos.y - p.y) < o.r + 1.2)) continue;
        if (torchAcc <= 0 && opts.torch && w.facing !== 'near' && wallH > 2.2) {
          torchAcc = kit.torchSpacing * (0.85 + rnd() * 0.3);
          const T = opts.torch;
          const q = { x: p.x + w.n.x * T.off, y: p.y + w.n.y * T.off };
          const y = w.foot + T.y;
          push({ asset: T.asset, pos: q, y, yaw: yawToward(inward.x, inward.y), scale: T.scale, tint: [1, 1, 1], layer: 'prop', cluster, r: 0.4, h: 1, owner, castShadow: false });
          const fp = { x: p.x + inward.x * T.flame[0], y: p.y + inward.y * T.flame[0] };
          out.flames.push({ pos: fp, y: y + T.flame[1], w: 0.55, h: 0.95, cluster });
          out.glows.push({ pos: { x: p.x + inward.x * 1.8, y: p.y + inward.y * 1.8 }, y: w.foot, r: 3.6, temp: 'warm', strength: 0.55, cluster });
          acc = Math.max(acc, 1.6);
          continue;
        }
        if (acc > 0) continue;
        acc = kit.wallSpacing * (0.7 + rnd() * 0.6);
        // Hanging vines / moss over the wall top.
        if (rnd() < kit.vines && w.facing !== 'near') {
          const strands = 2 + Math.floor(rnd() * 3);
          for (let i = 0; i < strands; i++) {
            const sc = 3.2 + rnd() * 1.4;
            const q = { x: p.x + ux * (i - strands / 2) * 0.45 + w.n.x * 1.6 * (sc / 3.5), y: p.y + uy * (i - strands / 2) * 0.45 + w.n.y * 1.6 * (sc / 3.5) };
            const hang = 1.5 * (sc / 3.5);
            out.placements.push({ asset: 'kn_hanging_moss', pos: q, y: w.top - hang - rnd() * 0.3, yaw: yawToward(inward.x, inward.y), scale: sc, stretch: [0.75, 1 + rnd() * 0.8, 1], tint: lerpTint([[0.32, 0.4, 0.28], [0.48, 0.56, 0.38]], rnd()), layer: 'detail', cluster, r: 0.2, h: 1.6, owner, castShadow: false });
          }
          continue;
        }
        const wi = pickWall(kit.wall, w.facing, rnd);
        if (!wi) continue;
        const sc = wi.scale[0] + rnd() * (wi.scale[1] - wi.scale[0]);
        // Fit under the wall top (banners on side walls shrink).
        const room = wallH - 0.1;
        const itemTop = wi.y + 3.73 * (wi.asset.includes('banner') ? 1 : 0.4);
        const fit = wi.asset.includes('banner') ? Math.min(1, room / Math.max(0.1, itemTop * sc)) : 1;
        if (fit < 0.55) continue;
        const scale = sc * fit;
        const q = { x: p.x + w.n.x * (wi.off ?? 0) * scale, y: p.y + w.n.y * (wi.off ?? 0) * scale };
        push({ asset: wi.asset, pos: q, y: w.foot + wi.y, yaw: yawToward(inward.x, inward.y), scale, tint: lerpTint(wi.tint, rnd()), layer: wi.detail ? 'detail' : 'prop', cluster, r: 0.3, h: 2, owner, castShadow: false });
      }
    }
  }

  /** A band of another pattern just inside the room's edge (a paved kerb around the floor). */
  function roomBorder(room: ArchLayout['rooms'][number], R: { x0: number; y0: number; x1: number; y1: number }, floor: string): void {
    const w = 1.6;
    if (R.x1 - R.x0 < 4 * w || R.y1 - R.y0 < 4 * w) return;
    const strips: Pt[][] = [
      [{ x: R.x0, y: R.y0 }, { x: R.x1, y: R.y0 }, { x: R.x1, y: R.y0 + w }, { x: R.x0, y: R.y0 + w }],
      [{ x: R.x0, y: R.y1 - w }, { x: R.x1, y: R.y1 - w }, { x: R.x1, y: R.y1 }, { x: R.x0, y: R.y1 }],
      [{ x: R.x0, y: R.y0 + w }, { x: R.x0 + w, y: R.y0 + w }, { x: R.x0 + w, y: R.y1 - w }, { x: R.x0, y: R.y1 - w }],
      [{ x: R.x1 - w, y: R.y0 + w }, { x: R.x1, y: R.y0 + w }, { x: R.x1, y: R.y1 - w }, { x: R.x1 - w, y: R.y1 - w }],
    ];
    out.borders.push({ polys: strips, width: w, floor, y: room.elevation, cluster: room.cluster, owner: room.id });
  }

  /**
   * Small islands of low debris on the open floor: away from pack spawns (fight space), the
   * entrance, doorways, paths and corridors; boss arenas only near their edges.
   */
  function debrisIslands(room: ArchLayout['rooms'][number], R: { x0: number; y0: number; x1: number; y1: number }, kit: PropKit, rnd: () => number): void {
    const items = kit.debris ?? [];
    const per = kit.islands ?? 0;
    if (!items.length || per <= 0) return;
    const arena = room.style.archetype.startsWith('arena');
    const areaYd = (R.x1 - R.x0) * (R.y1 - R.y0);
    const n = Math.round((areaYd / 1000) * per * (arena ? 0.6 : 1));
    const spawns: Vec2[] = [];
    for (const pk of d.packs) if (pk.roomId === room.id) for (const sp of pk.spawns) spawns.push(sp.pos);
    const paths = out.paths.filter((p) => p.owner === room.id);
    for (let i = 0, tries = 0; i < n && tries < n * 8; tries++) {
      const inset = 3;
      const c = { x: R.x0 + inset + rnd() * (R.x1 - R.x0 - 2 * inset), y: R.y0 + inset + rnd() * (R.y1 - R.y0 - 2 * inset) };
      if (arena) {
        const edge = Math.min(c.x - R.x0, R.x1 - c.x, c.y - R.y0, R.y1 - c.y);
        if (edge > 5.5) continue;
      }
      if (spawns.some((s) => Math.hypot(s.x - c.x, s.y - c.y) < ISLAND_CLEAR)) continue;
      if (Math.hypot(d.entrance.x - c.x, d.entrance.y - c.y) < 9) continue;
      if (paths.some((p) => p.pts.some((q, k) => k > 0 && distToSegment(c, p.pts[k - 1]!, q) < p.width / 2 + 2.5))) continue;
      if (arch.corridors.some((cr) => distToSegment(c, cr.axis.a, cr.axis.b) < cr.axis.width / 2 + 5)) continue;
      i++;
      out.decals.push({ pos: c, y: room.elevation, r: 1.8 + rnd() * 1.4, yaw: rnd() * Math.PI * 2, kind: rnd() < 0.5 ? 'moss' : 'cracks', cluster: room.cluster, alpha: 0.55 + rnd() * 0.3 });
      const m = 2 + Math.floor(rnd() * 3);
      for (let j = 0; j < m; j++) {
        const it = pickWeighted(items, rnd);
        if (!it) continue;
        const sc = it.scale[0] + rnd() * (it.scale[1] - it.scale[0]);
        const r = it.r * sc;
        const p = { x: c.x + (rnd() - 0.5) * 3, y: c.y + (rnd() - 0.5) * 3 };
        if (!arch.inWalkable(p) || arch.corridors.some((cr) => pointInConvex(p, cr.strip))) continue;
        if (spawns.some((s) => Math.hypot(s.x - p.x, s.y - p.y) < ISLAND_CLEAR - 1)) continue;
        if (!free(p, r)) continue;
        push({ asset: it.asset, pos: p, y: room.elevation + (it.lift ?? 0) * sc, yaw: rnd() * Math.PI * 2, scale: sc, tint: lerpTint(it.tint, rnd()), layer: 'detail', cluster: room.cluster, r, h: it.h * sc, owner: room.id, castShadow: false });
      }
    }
  }

  /** Worn paths from each doorway (corridor mouth) and the entrance to the room's centre. */
  function roomPaths(room: ArchLayout['rooms'][number], R: { x0: number; y0: number; x1: number; y1: number }, floor: string): void {
    const hub = { x: (R.x0 + R.x1) / 2, y: (R.y0 + R.y1) / 2 };
    const ends: { p: Pt; w: number }[] = [];
    for (const c of arch.corridors) {
      if (c.def.from !== room.id && c.def.to !== room.id) continue;
      // Where the corridor axis crosses the room edge (walk from the inside end outward).
      const inside = c.def.from === room.id ? c.axis.a : c.axis.b;
      const other = c.def.from === room.id ? c.axis.b : c.axis.a;
      let t = 0;
      for (; t < 1; t += 0.01) {
        const q = { x: inside.x + (other.x - inside.x) * t, y: inside.y + (other.y - inside.y) * t };
        if (q.x < R.x0 || q.x > R.x1 || q.y < R.y0 || q.y > R.y1) break;
      }
      const e = { x: inside.x + (other.x - inside.x) * Math.max(0, t - 0.01), y: inside.y + (other.y - inside.y) * Math.max(0, t - 0.01) };
      ends.push({ p: e, w: Math.max(4.5, c.axis.width * 0.62) });
    }
    const ent = d.entrance;
    if (ent.x >= R.x0 && ent.x <= R.x1 && ent.y >= R.y0 && ent.y <= R.y1) ends.push({ p: { x: ent.x, y: ent.y }, w: 6 });
    for (const e of ends) {
      out.paths.push({ pts: [e.p, hub], width: e.w, floor, y: room.elevation, cluster: room.cluster, owner: room.id });
    }
  }

  function pickWall(list: readonly WallItem[], facing: WallFacing, rnd: () => number): WallItem | null {
    // Near walls (toward the default camera) stay bare and low.
    if (facing === 'near') return null;
    return pickWeighted(list, rnd);
  }

  function nearOpening(p: Pt, r: number): boolean {
    return arch.posts.some((o) => o.kind === 'opening' && Math.hypot(o.pos.x - p.x, o.pos.y - p.y) < r) || arch.corridors.some((c) => distToSegment(p, c.axis.a, c.axis.b) < c.axis.width / 2 + arch.ledgeHeight * 0 + 2.2);
  }

  function floorDecals(room: ArchLayout['rooms'][number], kit: PropKit, rnd: () => number): void {
    const R = room.rect;
    if (!R || kit.decals <= 0) return;
    const areaYd = (R.x1 - R.x0) * (R.y1 - R.y0);
    const n = Math.round((areaYd / 100) * kit.decals);
    const clear: Vec2[] = [d.entrance];
    for (const pk of d.packs) if (pk.roomId === room.id) for (const sp of pk.spawns) clear.push(sp.pos);
    const kinds: DecalKindName[] = room.style.floor === 'dirt' || room.style.floor === 'dirtDark' ? ['roots', 'moss', 'cracks', 'roots'] : room.style.floor === 'wet' ? ['puddle', 'moss', 'puddle', 'cracks'] : ['moss', 'cracks', 'moss', 'puddle'];
    for (let i = 0; i < n; i++) {
      // Hug the room edges: pick an edge, inset 0.6..4.5 yd.
      const side = Math.floor(rnd() * 4);
      const t = rnd();
      const inset = 0.6 + rnd() * 3.9;
      const p =
        side === 0 ? { x: R.x0 + (R.x1 - R.x0) * t, y: R.y1 - inset } : side === 1 ? { x: R.x0 + (R.x1 - R.x0) * t, y: R.y0 + inset } : side === 2 ? { x: R.x0 + inset, y: R.y0 + (R.y1 - R.y0) * t } : { x: R.x1 - inset, y: R.y0 + (R.y1 - R.y0) * t };
      const r = 1.0 + rnd() * 2.2;
      if (clear.some((c) => Math.hypot(c.x - p.x, c.y - p.y) < 7 + r)) continue;
      const kind = kinds[Math.floor(rnd() * kinds.length)]!;
      out.decals.push({ pos: p, y: room.elevation, r, yaw: rnd() * Math.PI * 2, kind, cluster: room.cluster, alpha: 0.5 + rnd() * 0.35 });
    }
    // A few faint, broad patches across the open floor (wear, moss, cracks) so big rooms don't
    // read as one flat sheet. Kept faint and away from pack spawns so telegraphs stay clean.
    const arena = room.style.archetype.startsWith('arena');
    const m = Math.round((areaYd / 100) * kit.decals * (arena ? 0.25 : 0.45));
    for (let i = 0; i < m; i++) {
      const p = { x: R.x0 + 5 + rnd() * Math.max(0, R.x1 - R.x0 - 10), y: R.y0 + 5 + rnd() * Math.max(0, R.y1 - R.y0 - 10) };
      const r = 2 + rnd() * 2.5;
      const kind = kinds[Math.floor(rnd() * kinds.length)]!;
      const alpha = 0.28 + rnd() * 0.22;
      if (clear.some((c) => Math.hypot(c.x - p.x, c.y - p.y) < 6 + r)) continue;
      out.decals.push({ pos: p, y: room.elevation, r, yaw: rnd() * Math.PI * 2, kind, cluster: room.cluster, alpha });
    }
  }
}

function distToSegmentPoly(p: Pt, poly: readonly Pt[]): number {
  let d = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) d = Math.min(d, distToSegment(p, poly[j]!, poly[i]!));
  return d;
}
